// components/ui/Input.tsx
import { InputHTMLAttributes, forwardRef, useState } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    hint?: string;
    success?: string;
    isPassword?: boolean;
    leftIcon?: React.ReactNode;
    required?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ className, label, error, hint, success, id, type, isPassword, leftIcon, required, disabled, ...props }, ref) => {
        const [showPassword, setShowPassword] = useState(false);
        const [isFocused, setIsFocused] = useState(false);
        const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

        return (
            <div className="w-full">
                {label && (
                    <label
                        htmlFor={id}
                        className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5 uppercase tracking-wider"
                    >
                        {label}
                        {required && <span className="text-rose-500 ml-0.5">*</span>}
                    </label>
                )}
                <div className="relative group w-full">
                    {/* Focus ring glow */}
                    <div className={clsx(
                        'absolute -inset-0.5 rounded-xl opacity-0 transition-opacity duration-300 pointer-events-none',
                        isFocused && !error && 'opacity-100 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 blur',
                        isFocused && error && 'opacity-100 bg-gradient-to-r from-rose-500/20 to-red-500/20 blur',
                    )} />

                    <div className="relative w-full">
                        {leftIcon && (
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 dark:text-slate-500 transition-colors z-10">
                                {leftIcon}
                            </div>
                        )}

                        <input
                            ref={ref}
                            id={id}
                            type={inputType}
                            disabled={disabled}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            className={twMerge(clsx(
                                'w-full px-4 py-3.5 border-2 rounded-xl text-sm transition-all duration-200 relative z-10',
                                'focus:outline-none',
                                'placeholder:text-slate-400 dark:placeholder:text-slate-500',
                                'bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100',
                                'disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed dark:disabled:bg-slate-800/60 dark:disabled:text-slate-500',
                                leftIcon && 'pl-11',
                                (isPassword || error || success) && 'pr-11',
                                error
                                    ? 'border-rose-300 dark:border-rose-500/50 focus:border-rose-500'
                                    : success
                                        ? 'border-emerald-300 dark:border-emerald-500/50 focus:border-emerald-500'
                                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 focus:border-emerald-500',
                                className
                            ))}
                            {...props}
                        />

                        {/* Right icons */}
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
                            {error && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 400 }}
                                >
                                    <AlertCircle size={16} className="text-rose-500" />
                                </motion.div>
                            )}
                            {success && !error && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 400 }}
                                >
                                    <CheckCircle2 size={16} className="text-emerald-500" />
                                </motion.div>
                            )}
                            {isPassword && (
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Messages */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -5, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -5, height: 0 }}
                            className="flex items-center gap-1.5 mt-1.5"
                        >
                            <p className="text-xs text-rose-500 font-medium">{error}</p>
                        </motion.div>
                    )}
                    {hint && !error && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="mt-1.5 text-xs text-slate-400"
                        >
                            {hint}
                        </motion.p>
                    )}
                    {success && (
                        <motion.div
                            initial={{ opacity: 0, y: -5, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -5, height: 0 }}
                            className="flex items-center gap-1.5 mt-1.5"
                        >
                            <p className="text-xs text-emerald-500 font-medium">{success}</p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }
);

Input.displayName = 'Input';
export { Input };
export type { InputProps };